import { useAppSelector } from '@hooks/useRedux';
import {
  useCreateRatePlan,
  useRatePlans,
  useUpdateRatePlan,
} from '@hooks/accommodation';
import { selectAccommodationProperty } from '@store/accommodation';
import { Edit2, Plus, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  AccommodationMealPlan,
  AccommodationRatePlan,
} from '../../../../types/accommodation';
import { FormInput, FormSelect, FormTextarea } from '../../../common/FormField';

const MEAL_PLAN_LABELS: Record<AccommodationMealPlan, string> = {
  room_only: 'Room Only',
  breakfast: 'Bed & Breakfast',
  half_board: 'Half Board',
  full_board: 'Full Board',
};

interface PlanForm {
  name: string;
  description: string;
  meal_plan: AccommodationMealPlan | '';
  cancellation_policy: string;
}

const emptyPlanForm = (): PlanForm => ({
  name: '',
  description: '',
  meal_plan: '',
  cancellation_policy: '',
});

function RatePlanFormModal({
  editing,
  onClose,
  isSubmitting,
  onSubmit,
}: {
  editing: AccommodationRatePlan | null;
  onClose: () => void;
  isSubmitting: boolean;
  onSubmit: (form: PlanForm) => void;
}) {
  const [form, setForm] = useState<PlanForm>(
    editing
      ? {
          name: editing.name,
          description: editing.description ?? '',
          meal_plan: editing.meal_plan ?? '',
          cancellation_policy: editing.cancellation_policy ?? '',
        }
      : emptyPlanForm(),
  );
  const set = (key: keyof PlanForm, val: string) =>
    setForm((f) => ({ ...f, [key]: val }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">
            {editing ? 'Edit rate plan' : 'New rate plan'}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
          className="space-y-4"
        >
          <FormInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Non-Refundable"
          />
          <FormSelect
            label="Meal plan"
            value={form.meal_plan}
            onChange={(e) => set('meal_plan', e.target.value)}
          >
            <option value="">— none —</option>
            {(
              Object.entries(MEAL_PLAN_LABELS) as [
                AccommodationMealPlan,
                string,
              ][]
            ).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Cancellation policy"
            rows={3}
            value={form.cancellation_policy}
            onChange={(e) => set('cancellation_policy', e.target.value)}
            placeholder="e.g. Free cancellation up to 48 hours before check-in."
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const RatePlansPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const { data: allRatePlans = [] } = useRatePlans(property?.id);
  const ratePlans = allRatePlans.filter((rp) => rp.is_active);
  const createRatePlan = useCreateRatePlan();
  const updateRatePlan = useUpdateRatePlan();
  const isSubmitting = createRatePlan.isPending || updateRatePlan.isPending;

  const [editing, setEditing] = useState<AccommodationRatePlan | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = (form: PlanForm) => {
    if (!property) return;
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      meal_plan: (form.meal_plan as AccommodationMealPlan) || null,
      cancellation_policy: form.cancellation_policy.trim() || null,
    };
    const close = () => {
      setShowForm(false);
      setEditing(null);
    };
    if (editing) {
      updateRatePlan.mutate(
        { id: editing.id, updates: payload },
        { onSuccess: close },
      );
    } else {
      createRatePlan.mutate(
        { ...payload, property_id: property.id },
        { onSuccess: close },
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Rate plans</h3>
          <p className="text-xs text-gray-400">
            Configure pricing tiers and meal inclusions
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 rounded-xl bg-pos-primary px-3 py-2 text-xs font-medium text-white hover:bg-pos-primary/90"
        >
          <Plus size={13} />
          Add plan
        </button>
      </div>

      {ratePlans.length === 0 ? (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          No rate plans yet
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Name
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Meal plan
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500">
                  Cancellation policy
                </th>
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ratePlans.map((plan) => (
                <tr key={plan.id}>
                  <td className="px-5 py-3 font-medium text-gray-900">
                    {plan.name}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {plan.meal_plan ? MEAL_PLAN_LABELS[plan.meal_plan] : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {plan.cancellation_policy
                      ? plan.cancellation_policy.slice(0, 60) +
                        (plan.cancellation_policy.length > 60 ? '…' : '')
                      : '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(plan);
                        setShowForm(true);
                      }}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <Edit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RatePlanFormModal
          editing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};
